import type { SupSingleVaultV3, SupStoredFavToken } from "@/types";
import type { Address } from "viem";

import React, { useMemo, useState } from "react";

import { type Column, SupIconButton, SupDataGrid, SupText, SupTooltip, SupInput } from "@repo/core-ui";
import { useStoredData } from "@/store/Context";
import { useReduceVaultAssets } from "@/hooks/data/filters/useReduceUnderlyingAssets";
import GridCellPinToken from "@/components/Grid/Cell/PinToken";
import UiPinnedToken from "@/components/Ui/PinnedToken";
import UiTag from "@/components/Ui/Tag";
import { SupSearch, SupIconCheckbox, SupCheckboxChecked } from "@repo/core-ui";
import { isAddressEqual, isAddress } from "viem";

interface GridBarFiltersAtomTokenProps {
  clickHandler?: (newToken: SupStoredFavToken) => void;
  selectedTokens?: SupStoredFavToken[];
  vaults?: SupSingleVaultV3[];
}

export default function GridBarFiltersAtomTokens({ clickHandler, selectedTokens, vaults }: GridBarFiltersAtomTokenProps) {
  const supportedTokens = useReduceVaultAssets({ vaults });

  const [query, setQuery] = useState<string>("");

  const storedData = useStoredData();

  const mappedTokenBalances = useMemo(() => {
    return supportedTokens?.filter(
      singleToken => storedData.favTokens.find(favToken => favToken.tokenAddress == singleToken.tokenAddress && favToken.chainId == singleToken.chainId) !== undefined
    ) as SupStoredFavToken[];
  }, [supportedTokens, storedData.favTokens]);

  const columns: Column<SupStoredFavToken>[] = [
    {
      minWidth: 30,
      maxWidth: 30,
      field: "select",
      headerName: "",
      sortable: false,
      draggable: false,
      cellClassName: "cursor-pointer",
      resizable: false,
      valueGetter: row => `${row.tokenSymbol}_${row.tokenAddress}`,
      renderCell: row => (
        <SupIconButton
          active={selectedTokens?.find(item => item.tokenAddress == row.tokenAddress && item.chainId == row.chainId) !== undefined}
          activeIcon={<SupCheckboxChecked />}
        >
          <SupIconCheckbox />
        </SupIconButton>
      ),
    },
    {
      minWidth: 310,
      maxWidth: 310,
      draggable: false,
      resizable: false,
      field: "symbol",
      cellClassName: "cursor-pointer",
      headerName: "Token",
      valueGetter: row => `${row.tokenSymbol}_${row.tokenAddress}`,
      renderCell: row => (
        <SupTooltip content={row.tokenAddress} portal color="primary" placement={"top-end"}>
          <div>
            <UiTag asset={{ icon: row.tokenImage, symbol: `${row.tokenSymbol}`, name: `${row.tokenSymbol}` }} chainId={row.chainId} />
          </div>
        </SupTooltip>
      ),
    },
    {
      minWidth: 30,
      maxWidth: 30,
      resizable: false,
      sortable: false,
      draggable: false,
      field: "pin",
      headerName: "",
      renderCell: row => (
        <GridCellPinToken
          singleBalance={{
            address: row.tokenAddress,
            chainId: row.chainId,
            symbol: `${row.tokenSymbol}`,
          }}
        />
      ),
    },
  ];

  const filteredSupportedTokens = useMemo(() => {
    let results = supportedTokens ? supportedTokens.slice() : ([] as SupStoredFavToken[]);
    if (results.length === 0) {
      return results;
    }
    if (query.length > 1) {
      results = results.filter(token => {
        return isAddress(query) ? isAddressEqual(query as Address, token.tokenAddress) : `${token.tokenSymbol}`.toLowerCase().includes(query.toLowerCase());
      });
    }
    return results;
  }, [query, supportedTokens]);

  return (
    <div className="mt-[16px] space-y-[16px]">
      <SupInput
        clearable
        type="text"
        prefixIcon={<SupSearch />}
        id="balance-search"
        value={query}
        fluid
        inputProps={{
          className: "normal-case pr-10",
        }}
        placeholder="SEARCH SYMBOL/ADDRESS"
        onChange={e => setQuery(e ?? "")}
      />
      {mappedTokenBalances.length > 0 && (
        <>
          <SupText variant="subtitle1" as="h3">
            Pinned tokens
          </SupText>
          <ul className="flex flex-wrap items-center gap-[10px]">
            {mappedTokenBalances.map(pinnedBalance => (
              <UiPinnedToken
                key={`token-${pinnedBalance.tokenAddress}-${pinnedBalance.chainId}`}
                clickHandler={clickHandler}
                isSelected={selectedTokens?.find(item => item.tokenAddress === pinnedBalance.tokenAddress && item.chainId === pinnedBalance.chainId) !== undefined}
                token={{
                  ...pinnedBalance,
                  icon: pinnedBalance.tokenImage,
                  symbol: pinnedBalance.tokenSymbol || "",
                  name: pinnedBalance.tokenSymbol || "",
                  chainId: pinnedBalance.chainId,
                }}
              />
            ))}
          </ul>
        </>
      )}

      <SupDataGrid
        onRowClick={row => clickHandler?.(Array.isArray(row) ? row[0] : row)}
        className="h-full min-h-[240px]"
        data={filteredSupportedTokens}
        defaultSorting={[{ field: "symbol", direction: "ASC" }]}
        rowId={row => `${row.tokenAddress}_${row.chainId}`}
        columns={columns}
      />
    </div>
  );
}
